import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { testIncidentWhatsAppMutation } from "@/api/queries";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { SelectField } from "@/components/ui/field";
import { isAppLocale, type AppLocale } from "@/i18n/locales";
import { useApiClient } from "@/lib/api-context";

/**
 * Super-admin probe for the WhatsApp Cloud API and the incident template.
 *
 * Does not open an incident. Independent of the platform WhatsApp switch, so
 * the token can be checked while incident WhatsApp stays off. A simulator
 * configuration is a failure: this control exists to show Meta accepted
 * the template.
 */
export function TestIncidentWhatsAppCard() {
  const { t } = useTranslation("pages");
  const client = useApiClient();
  const [to, setTo] = useState("");
  const [locale, setLocale] = useState<AppLocale>("es-AR");

  const mutation = useMutation({
    ...testIncidentWhatsAppMutation(client),
  });

  const result = mutation.data;

  return (
    <Card className="mb-4">
      <CardBody>
        <div className="space-y-1">
          <h2 className="text-sm font-semibold text-ink">{t("testIncidentWhatsAppTitle")}</h2>
          <p className="text-sm text-ink-muted">{t("testIncidentWhatsAppDescription")}</p>
        </div>

        <div className="mt-3 flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <label htmlFor="test-incident-whatsapp-to" className="text-sm font-medium text-ink">
              {t("testIncidentWhatsAppTo")}
            </label>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <input
                id="test-incident-whatsapp-to"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={to}
                onChange={(event) => setTo(event.target.value)}
                placeholder="+5493511234567"
                aria-describedby="test-incident-whatsapp-to-hint"
                className="w-full min-w-0 flex-1 rounded-md border border-border-subtle bg-surface px-3.5 py-2.5 text-sm text-ink placeholder:text-ink-muted/70 focus-visible:border-brand focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand"
              />
              <Button
                className="shrink-0 sm:self-auto"
                loading={mutation.isPending}
                disabled={!to.trim() || mutation.isPending}
                onClick={() => mutation.mutate({ to: to.trim(), locale })}
              >
                {t("testIncidentWhatsAppSend")}
              </Button>
            </div>
            <p id="test-incident-whatsapp-to-hint" className="text-xs text-ink-muted">
              {t("testIncidentWhatsAppToHint")}
            </p>
          </div>

          <SelectField
            className="sm:max-w-xs"
            label={t("testIncidentWhatsAppLocale")}
            value={locale}
            onChange={(value) => {
              if (isAppLocale(value)) {
                setLocale(value);
              }
            }}
            data={[
              { value: "es-AR", label: t("testIncidentWhatsAppLocaleEs") },
              { value: "en-GB", label: t("testIncidentWhatsAppLocaleEn") },
            ]}
          />
        </div>

        {mutation.isError ? (
          <Alert className="mt-3" tone="danger">
            {mutation.error.message}
          </Alert>
        ) : null}

        {result?.delivered === false ? (
          <Alert className="mt-3" tone="danger" title={t("testIncidentWhatsAppFailedTitle")}>
            {result.error ?? t("testIncidentWhatsAppFailedFallback")}
          </Alert>
        ) : null}

        {result?.delivered ? (
          <Alert className="mt-3" tone="info" title={t("testIncidentWhatsAppSentTitle")} announce>
            {t("testIncidentWhatsAppSent", {
              to: result.to,
              template: result.template,
              language: result.language,
            })}
          </Alert>
        ) : null}
      </CardBody>
    </Card>
  );
}
